import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Star, Loader, CheckCircle2, XCircle, X, CreditCard, Smartphone } from 'lucide-react';
import { AppLang } from '../i18n';
import './SubscriptionPage.css';

interface SubscriptionPageProps {
  lang: AppLang;
  onBack: () => void;
}

type Tier = 'standard' | 'premium';

// Должно совпадать с _PRICE_MATRIX и _PREMIUM_PRICE_MATRIX в payment_platega.py
const PRICE_MATRIX: Record<number, Record<number, number>> = {
  1:  { 1: 299,  2: 550,  3: 780,  4: 990,  5: 1180 },
  3:  { 1: 749,  2: 1390, 3: 1950, 4: 2470, 5: 2960 },
  6:  { 1: 1290, 2: 2390, 3: 3350, 4: 4260, 5: 5100 },
  12: { 1: 2090, 2: 3870, 3: 5430, 4: 6900, 5: 8260 },
};

const PREMIUM_PRICE_MATRIX: Record<number, number> = {
  1: 449, 3: 1099, 6: 2199, 12: 3499,
};

function calcPrice(months: number, devices: number, tier: Tier): number {
  if (tier === 'premium') return PREMIUM_PRICE_MATRIX[months] ?? 0;
  return PRICE_MATRIX[months]?.[devices] ?? 0;
}

interface Plan {
  id: string;
  months: number;
  label: string;
  discount: number;
  recommended?: boolean;
}

const PLANS: Plan[] = [
  { id: '1m', months: 1, label: '1 месяц', discount: 0 },
  { id: '3m', months: 3, label: '3 месяца', discount: 17 },
  { id: '6m', months: 6, label: '6 месяцев', discount: 28, recommended: true },
  { id: '12m', months: 12, label: '1 год', discount: 42 },
];

type CheckoutState = 'idle' | 'loading' | 'open' | 'polling' | 'success' | 'failed' | 'timeout';

export const SubscriptionPage = ({ lang: _lang, onBack }: SubscriptionPageProps) => {
  void _lang;
  const tier: Tier = 'standard';           // единый базовый тариф
  const [devices, setDevices] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<string>('6m');
  const [payMethod, setPayMethod] = useState<'sbp' | 'card' | 'crypto'>('sbp');
  const [checkoutState, setCheckoutState] = useState<CheckoutState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const [promo, setPromo] = useState<{ active: boolean; bonus_days: number; months: number; until_human: string } | null>(null);

  useEffect(() => {
    fetch('/app-api/promo')
      .then(r => r.json())
      .then(d => setPromo(d))
      .catch(() => {});
  }, []);

  const plan = PLANS.find(p => p.id === selectedPlan)!;
  const totalPrice = calcPrice(plan.months, devices, tier);
  const perMonth = Math.round(totalPrice / plan.months);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollCountRef.current = 0;
  }, []);

  const handleCloseCheckout = useCallback(() => {
    stopPolling();
    setCheckoutState('idle');
  }, [stopPolling]);

  useEffect(() => () => { stopPolling(); }, [stopPolling]);

  const startPolling = useCallback((paymentId: string) => {
    stopPolling();
    const tg = (window as any).Telegram?.WebApp;
    const initData = tg?.initData ?? '';
    const pollAuth = initData
      ? `tma ${initData}`
      : `Bearer ${localStorage.getItem('hits_jwt') || ''}`;
    pollCountRef.current = 0;
    pollIntervalRef.current = setInterval(async () => {
      pollCountRef.current += 1;
      if (pollCountRef.current > 79) { stopPolling(); setCheckoutState('timeout'); return; }
      try {
        const resp = await fetch(`/app-api/payment/platega/status/${paymentId}`, {
          headers: { Authorization: pollAuth },
        });
        const data = await resp.json();
        if (data.status === 'succeeded' || data.paid === true) { stopPolling(); setCheckoutState('success'); }
        else if (data.status === 'canceled') { stopPolling(); setCheckoutState('failed'); }
      } catch { /* keep polling */ }
    }, 3009);
  }, [stopPolling]);

  const handlePay = async () => {
    if (checkoutState === 'loading') return;
    setErrorMsg('');
    setCheckoutState('loading');
    try {
      const tg = (window as any).Telegram?.WebApp;
      const initData = tg?.initData ?? '';
      const methodCode = payMethod === 'sbp' ? 2 : payMethod === 'card' ? 11 : 13;
      const authHeader = initData
        ? `tma ${initData}`
        : `Bearer ${localStorage.getItem('hits_jwt') || ''}`;
      const resp = await fetch('/app-api/payment/platega/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: authHeader },
        body: JSON.stringify({
          amount: totalPrice,
          months: plan.months,
          devices,
          tier,
          payment_method: methodCode,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.confirmation_url) {
        const msg = data.error === 'payment_pending'
          ? 'У вас уже есть активный платёж. Дождитесь его завершения.'
          : 'Не удалось создать платёж. Попробуй ещё раз.';
        setErrorMsg(msg);
        setCheckoutState('idle');
        return;
      }
      if (tg?.openLink) tg.openLink(data.confirmation_url, { try_instant_view: false });
      else window.open(data.confirmation_url, '_blank');
      setCheckoutState('polling');
      startPolling(data.payment_id);
    } catch {
      setErrorMsg('Не удалось создать платёж. Попробуй ещё раз.');
      setCheckoutState('idle');
    }
  };

  return (
    <div className="sub">
      <div className="sub-header">
        <button className="sub-back" onClick={onBack}><ArrowLeft size={20} /></button>
      </div>

      <motion.div className="sub-content" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="sub-title">Покупка подписки</h1>
        <p className="sub-subtitle">Базовая подписка PATRiK VPN ⚓</p>

        {/* === СЛАЙДЕР УСТРОЙСТВ (1–5) === */}
        <div className="sub-devices-card">
          <div className="sub-devices-top">
            <div className="sub-devices-num">{devices}</div>
            <div className="sub-devices-info">
              <span className="sub-devices-label">
                {devices === 1 ? 'Устройство' : devices < 5 ? 'Устройства' : 'Устройств'}
              </span>
              <span className="sub-devices-sub">Одновременно в подписке</span>
            </div>
          </div>
          <input
            type="range" min={1} max={5} value={devices}
            onChange={e => setDevices(Number(e.target.value))}
            className="sub-devices-slider"
          />
          <div className="sub-devices-dots">
            {[1, 2, 3, 4, 5].map(n => (
              <span key={n} className={`sub-dot ${n <= devices ? 'active' : ''}`} />
            ))}
          </div>
        </div>

        {/* === АКЦИЯ === */}
        {promo?.active && (
          <div className="sub-promo-banner">
            <span className="sub-promo-main">🎁 +{promo.bonus_days} дней в подарок при оплате на 1 месяц</span>
            <span className="sub-promo-until">Акция до {promo.until_human}</span>
          </div>
        )}

        {/* === СЕТКА ПЛАНОВ === */}
        <div className="sub-plans-grid">
          {PLANS.map(p => {
            const price = calcPrice(p.months, devices, tier);
            const pm = Math.round(price / p.months);
            return (
              <button
                key={p.id}
                className={`sub-plan ${selectedPlan === p.id ? 'selected' : ''} ${p.recommended ? 'recommended' : ''}`}
                onClick={() => setSelectedPlan(p.id)}
              >
                {p.recommended && <Star size={16} className="sub-plan-star" />}
                {p.recommended && <span className="sub-plan-badge">🔥 Выгодно</span>}
                {p.discount > 0 && <span className="sub-plan-discount">-{p.discount}%</span>}
                <span className="sub-plan-label">{p.label}</span>
                <span className="sub-plan-price">{price} ₽</span>
                <span className="sub-plan-per-month">{pm} ₽/мес</span>
                {promo?.active && p.months === promo.months && (
                  <span className="sub-plan-gift">🎁 +{promo.bonus_days} дней</span>
                )}
              </button>
            );
          })}
        </div>

        {/* === МЕТОД ОПЛАТЫ === */}
        <div className="sub-methods">
          <button
            className={`sub-method ${payMethod === 'card' ? 'selected' : ''}`}
            onClick={() => setPayMethod('card')}
          >
            <CreditCard size={18} />
            <span>Карта</span>
          </button>
          <button
            className={`sub-method ${payMethod === 'sbp' ? 'selected' : ''}`}
            onClick={() => setPayMethod('sbp')}
          >
            <Smartphone size={18} />
            <span>СБП</span>
            <Star size={12} className="sub-method-star" />
          </button>
          <button
            className={`sub-method ${payMethod === 'crypto' ? 'selected' : ''}`}
            onClick={() => setPayMethod('crypto')}
          >
            <span style={{ fontSize: 18 }}>&#x20BF;</span>
            <span>Крипта</span>
          </button>
        </div>

        {errorMsg && <div className="sub-error">{errorMsg}</div>}

        {/* === КНОПКА ОПЛАТЫ === */}
        <button className="sub-pay-btn" onClick={handlePay} disabled={checkoutState !== 'idle'}>
          {checkoutState === 'loading' ? <Loader size={20} className="spin" /> : null}
          <span>{checkoutState === 'loading' ? 'Создаём платёж...' : 'Оплатить подписку'}</span>
          {checkoutState !== 'loading' && (
            <span className="sub-pay-price">{totalPrice} ₽</span>
          )}
        </button>

        <div className="sub-info">
          {perMonth} ₽ в месяц • {devices} {devices < 5 ? 'устройства' : 'устройств'}
        </div>
      </motion.div>

      {/* Payment modal */}
      <AnimatePresence>
        {checkoutState !== 'idle' && (
          <motion.div className="sub-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="sub-modal" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}>
              <div className="sub-modal-header">
                <span className="sub-modal-title">Оплата</span>
                <button className="sub-modal-close" onClick={handleCloseCheckout}><X size={22} /></button>
              </div>
              {checkoutState === 'success' && (
                <div className="sub-result sub-result--success">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 189, damping: 20 }}>
                    <CheckCircle2 size={64} strokeWidth={1.5} />
                  </motion.div>
                  <h3>Оплачено!</h3>
                  <p>Подписка продлена. VPN активен.</p>
                  <button className="sub-result-btn" onClick={handleCloseCheckout}>Отлично</button>
                </div>
              )}
              {checkoutState === 'failed' && (
                <div className="sub-result sub-result--failed">
                  <XCircle size={64} strokeWidth={1.5} />
                  <h3>Платёж отменён</h3>
                  <p>Попробуй ещё раз или выбери другой способ оплаты.</p>
                  <button className="sub-result-btn" onClick={handleCloseCheckout}>Попробовать снова</button>
                </div>
              )}
              {checkoutState === 'timeout' && (
                <div className="sub-result sub-result--failed">
                  <XCircle size={64} strokeWidth={1.5} />
                  <h3>Статус неизвестен</h3>
                  <p>Не удалось подтвердить оплату. Если вы оплатили — подписка будет начислена автоматически.</p>
                  <button className="sub-result-btn" onClick={handleCloseCheckout}>Закрыть</button>
                </div>
              )}
              {checkoutState === 'polling' && (
                <div className="sub-result sub-result--polling">
                  <Loader size={48} className="sub-spin" />
                  <h3>Ожидаем оплату...</h3>
                  <p>Страница оплаты открыта в браузере. После оплаты подписка продлится автоматически.</p>
                  <button className="sub-result-btn sub-result-btn--outline" onClick={handleCloseCheckout}>Отмена</button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
